import { ReactNode, isValidElement, useRef } from 'react';
import { useDrag, useDrop, type DropTargetMonitor } from 'react-dnd';

const ItemType = 'SORTABLE_ITEM';

interface DragItemReturn {
  isDragging: boolean;
  drag: ReturnType<typeof useDrag>[1];
  drop: ReturnType<typeof useDrop>[1];
  opacity: number;
  preview: ReturnType<typeof useDrag>[2];
}

interface DragItem {
  index: number;
  type: typeof ItemType;
}

const DragItem = ({
  children,
  moveItem,
  index,
}: {
  children?: ReactNode | ((props: DragItemReturn) => ReactNode);
  moveItem: (dragIndex: number, hoverIndex: number) => void;
  index: number;
}) => {
  const dropRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef(null);

  const [, drop] = useDrop({
    accept: ItemType,
    hover(draggedItem: DragItem, monitor: DropTargetMonitor) {
      if (!dropRef.current) return;

      const dragIndex = draggedItem.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) return;

      // Determine rectangle on screen
      const hoverBoundingRect = dropRef.current.getBoundingClientRect();
      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      // Determine mouse position
      const clientOffset = monitor.getClientOffset();
      // Get pixels to the top
      const hoverClientY = clientOffset ? clientOffset.y - hoverBoundingRect.top : 0;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

      moveItem(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      draggedItem.index = hoverIndex;
    },
  });

  const [{ isDragging, opacity }, drag, preview] = useDrag({
    type: ItemType,
    item: { type: ItemType, index } satisfies DragItem,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
      opacity: monitor.isDragging() ? 0.3 : 1,
    }),
  });

  preview(drop(dropRef));
  drag(dragRef);

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <div ref={dropRef} style={{ opacity: isDragging ? 0.5 : 1 }}>
      <div ref={dragRef}>
        {isValidElement(children) || typeof children !== 'function'
          ? children
          : children?.({ isDragging, drag, drop, opacity, preview })}
      </div>
    </div>
  );
};

DragItem.displayName = 'DragItem';

export default DragItem;
